import { useCallback, useEffect, useRef } from 'react';
import { POLL_BACKOFF_MAX_DOUBLINGS, POLL_ELECTION_MS, POLL_MAX_BACKOFF_MS } from '../constants/network';

/** One polling owner per authenticated scope; followers receive snapshots in memory. */
export default function useSharedPolling({ scope, enabled, load, onData, interval, allowHidden }) {
  const handlers = useRef({ load, onData, allowHidden });
  handlers.current = { load, onData, allowHidden };
  const runner = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    let leader = false;
    let acquiring = false;
    let release;
    let timer;
    let election;
    let pending;
    let latest;
    let failures = 0;
    const channelName = `neoprof:poll:${scope}`;
    let channel = null;
    try {
      if (typeof BroadcastChannel === 'function') channel = new BroadcastChannel(channelName);
    } catch { /* Private browsing can disable cross-tab coordination. */ }
    let coordinated = Boolean(navigator.locks?.request && channel);
    const eligible = () => navigator.onLine !== false
      && (document.visibilityState !== 'hidden' || handlers.current.allowHidden?.());

    const refresh = () => {
      if (pending) return pending;
      pending = Promise.resolve().then(() => handlers.current.load()).then((data) => {
        if (alive) {
          latest = data;
          handlers.current.onData(data);
          channel?.postMessage({ type: 'snapshot', data });
        }
        return data;
      }).finally(() => { pending = null; });
      return pending;
    };
    runner.current = refresh;
    // One timer at most: every path re-arms through here, so concurrent polls never fork the loop.
    const schedule = () => {
      clearTimeout(timer);
      if (alive && leader && eligible()) {
        const backoff = 2 ** Math.min(failures, POLL_BACKOFF_MAX_DOUBLINGS);
        timer = setTimeout(poll, Math.min(interval * backoff, POLL_MAX_BACKOFF_MS));
      }
    };
    const poll = async () => {
      if (!alive || !leader || !eligible()) return;
      clearTimeout(timer);
      try { await refresh(); failures = 0; }   // joins an in-flight refresh instead of skipping the re-arm
      catch { failures += 1; }
      schedule();
    };
    const stopLeading = () => {
      clearTimeout(timer);
      release?.();
      release = undefined;
      leader = false;
    };
    const elect = async () => {
      clearTimeout(election);
      if (!alive) return;
      if (!eligible()) stopLeading();
      else if (!leader && !acquiring) {
        if (coordinated) {
          acquiring = true;
          try {
            await navigator.locks.request(channelName, { ifAvailable: true }, async (lock) => {
              if (!lock || !alive || !eligible()) return;
              leader = true;
              // Keep the lock for the lifetime of ownership, including waits
              // between requests. A closed/hidden owner releases it for another tab.
              const ownership = new Promise((resolve) => { release = resolve; });
              void poll();
              await ownership;
            });
          } catch {
            coordinated = false;
            if (alive && eligible()) { leader = true; void poll(); }
          } finally { acquiring = false; }
        } else {
          // Older browsers still get visibility gating, coalescing and backoff.
          leader = true;
          void poll();
        }
      }
      if (alive) election = setTimeout(elect, POLL_ELECTION_MS);
    };
    if (channel) {
      channel.onmessage = ({ data: message }) => {
        if (!alive) return;
        if (message?.type === 'snapshot') {
          latest = message.data;
          handlers.current.onData(message.data);
        } else if (message?.type === 'request' && leader && latest !== undefined) {
          channel.postMessage({ type: 'snapshot', data: latest });
        }
      };
      channel.postMessage({ type: 'request' });
    }
    const wake = () => {
      if (!eligible()) stopLeading();
      if (leader) { clearTimeout(timer); void poll(); }
      else void elect();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    window.addEventListener('offline', wake);
    void elect();
    return () => {
      alive = false;
      runner.current = null;
      clearTimeout(election);
      stopLeading();
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('offline', wake);
      channel?.close();
    };
  }, [scope, enabled, interval]);

  return useCallback(() => runner.current?.() || Promise.resolve(), []);
}
